import { GlobalContext } from "context/Provider";
import React, { useContext } from "react";
import { Chip, Box, Typography } from "@mui/material";
import { SxProps } from "@mui/material";
import { SPIROMETRY_KEY_MAP} from "helpers/variables";

interface HiddenFieldsChipsProps {
  hiddenFields: string[];
  labels: Record<string, string>;
  onRestore: (field: string) => void;
  title?: string;
  sx?: SxProps;
}

export const spirometryLabels: Record<string, string> = {
  fev1: "FEV1",
  fvc: "FVC",
  fev1fvc: "FEV1/FVC",
  pef: "PEF",
};

export const defaultSpirometryVisibility = {
  fev1: true,
  fvc: true,
  fev1fvc: true,
  pef: true,
};

export const useSpirometryVisibility = () => {
  const { settings, settingsDispatch } = useContext(GlobalContext);
  const defaultSettings =
    settings?.default?.data?.system?.settings as
      | Record<string, Record<string, boolean>>
      | undefined;

  const spirDefaults = defaultSettings?.spir || {};
  const savedVisibility =
    settings?.member?.data?.system?.settings?.spirometryVisibility ||
    defaultSpirometryVisibility;

  const visibility: Record<string, boolean> = {};
  Object.keys(SPIROMETRY_KEY_MAP).forEach((field) => {
    const defaultKey = SPIROMETRY_KEY_MAP[field];
    const defaultAllows = spirDefaults[defaultKey] !== false;
    const userAllows = savedVisibility[field] !== false;
    visibility[field] = defaultAllows && userAllows;
  });

  const hiddenFields = Object.entries(visibility)
    .filter(([field, isVisible]) => {
      if (!isVisible) {
        const spirGroup = defaultSettings?.spir;
        const defaultKey = SPIROMETRY_KEY_MAP[field];
        return spirGroup?.[defaultKey] !== false;
      }
      return false;
    })
    .map(([field]) => field);

  const updateVisibility = (newModel: Record<string, boolean>) => {
    settingsDispatch({
      type: "UPDATE_MEMBER_SETTINGS",
      payload: {
        spirometryVisibility: newModel,
      },
    });
  };

  return {
    visibility,
    hiddenFields,
    updateVisibility,
  };
};

export const HiddenFieldsChips = ({
  hiddenFields,
  labels,
  onRestore,
  title = "Hidden Fields",
  sx,
}: HiddenFieldsChipsProps) => {
  if (!hiddenFields.length) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2, ...sx }}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {hiddenFields.map((field: string) => (
          <Chip
            key={field}
            label={labels[field]}
            onDelete={() => onRestore(field)}
          />
        ))}
      </Box>
    </Box>
  );
};